import { InboxOutlined } from '@ant-design/icons';
import { AuthRoles } from '@hefe/auth';
import { t } from '@hefe/i18n';
import { SelectOption, SelectOptionGroup } from '@hefe/models';
import { ModalsSlice, currentUserSelector } from '@hefe/store';
import { dateFormat, timeFormat } from '@hefe/utils';
import {
  Button,
  Checkbox,
  ColorPicker,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  TimePicker,
  TreeSelect,
  Typography,
  Upload,
} from 'antd';
import { DataNode } from 'antd/es/tree';
import { Rule } from 'antd/lib/form';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import EditorComponent from '../editor/editor.component';
import './custom-form.module.scss';

/* eslint-disable-next-line */
export interface FormProps {
  loading?: boolean;
  formId?: string;
  onSubmit: (values: any, resetFields?: () => void) => void;
  submitButtonText?: string;
  fields: FormField[];
  refresh?: boolean;
  backButton?: boolean;
  buttonsOffset?: number;
}

export type FormFieldType =
  | 'checkbox'
  | 'colorpicker'
  | 'date'
  | 'daterange'
  | 'datetime'
  | 'datetimerange'
  | 'email'
  | 'heading'
  | 'image'
  | 'number'
  | 'password'
  | 'radiogroup'
  | 'select'
  | 'select_groups'
  | 'text'
  | 'texteditor'
  | 'textarea'
  | 'time'
  | 'tree_select'
  | 'upload';

export interface FormField {
  type: FormFieldType;
  name: string;
  label: string;
  extraLabel?: string | React.ReactNode;
  rules: Rule[];
  placeholder?: string;
  initalValue?: any;
  hidden?: boolean;
  subscription?: string[];
  /* select specific */
  selectOptions?: SelectOption[] | SelectOptionGroup[];
  multiSelect?: boolean;
  disableSelectClear?: boolean;
  /* checkbox specific */
  checkboxText?: string;
  /* necessary if dynamic form field */
  value?: any;
  additionalProps?: any;
  onChange?: (value: any) => void;
  /* width */
  colWidth?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xxl?: number;
  };
}

export function CustomForm(props: FormProps) {
  const [form] = Form.useForm();

  const dispatch = useAppDispatch();

  const user = useSelector(currentUserSelector);
  const modalState = useSelector(ModalsSlice.modalsState).modal;

  const inputNumberRef = useRef<any>(null);

  useEffect(() => {
    form.resetFields();
  }, [form, props.refresh]);

  useEffect(() => {
    if (props.formId && modalState.formId === props.formId) {
      const setModalLoading = ModalsSlice.modalsActions.setModalLoading;
      dispatch(setModalLoading(props.loading));
    }
  }, [props.loading, props.formId, modalState.formId, dispatch]);

  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };

  const initialValues = () => {
    const values: any = {};

    props.fields.forEach((field: FormField) => {
      if (field.initalValue !== undefined) {
        switch (field.type) {
          case 'time':
            values[field.name] = dayjs(
              `${dayjs().format('YYYY-MM-DD')} ${field.initalValue}:00`
            );
            break;
          case 'date':
            values[field.name] = dayjs(
              `${dayjs(field.initalValue).format('YYYY-MM-DD')}`
            );
            break;
          case 'datetime':
            values[field.name] = dayjs(
              `${dayjs(field.initalValue).format('YYYY-MM-DD HH:mm:ss')}`
            );
            break;
          default:
            values[field.name] = field.initalValue;
            break;
        }
      }
    });

    return values;
  };

  const inputField = (field: FormField) => {
    if (field.hidden && field.subscription) {
      return (
        <Typography.Text>
          {t(`errors.permissions.${field.subscription[0]}`)}
        </Typography.Text>
      );
    }

    switch (field.type) {
      case 'text':
      case 'email':
      case 'password':
        return (
          <Input
            name={field.name}
            placeholder={field.placeholder}
            type={field.type}
            autoComplete={field.type === 'password' ? 'true' : 'false'}
            autoCorrect={field.type === 'password' ? 'true' : 'false'}
          />
        );
      case 'textarea':
        return (
          <Input.TextArea
            name={field.name}
            placeholder={field.placeholder}
            {...field.additionalProps}
          />
        );
      case 'number':
        return (
          <InputNumber
            name={field.name}
            ref={inputNumberRef}
            placeholder={field.placeholder}
            onChange={() => {
              setTimeout(() => {
                inputNumberRef.current?.focus();
              }, 50);
            }}
          />
        );
      case 'date':
        return (
          <DatePicker
            name={field.name}
            placeholder={field.placeholder}
            format={dateFormat()}
            style={{ width: '100%' }}
          />
        );
      case 'time':
        return (
          <TimePicker
            name={field.name}
            placeholder={field.placeholder}
            format={timeFormat()}
          />
        );
      case 'datetime':
        return (
          <DatePicker
            name={field.name}
            placeholder={field.placeholder}
            showTime
            format={dateFormat() + ' HH:mm'}
            style={{ width: '100%' }}
            {...(field.additionalProps ? field.additionalProps : {})}
          />
        );
      case 'daterange':
        return (
          <DatePicker.RangePicker
            name={field.name}
            format={dateFormat()}
            style={{ width: '100%' }}
          />
        );
      case 'datetimerange':
        return (
          <DatePicker.RangePicker
            name={field.name}
            showTime
            format={dateFormat() + ' HH:mm'}
            style={{ width: '100%' }}
          />
        );
      case 'heading':
        return <Typography.Title level={5}>{field.label}</Typography.Title>;
      case 'select':
        return (
          <Select
            id={field.name}
            placeholder={field.placeholder}
            showSearch
            allowClear={!field.disableSelectClear}
            {...(field.multiSelect ? { mode: 'multiple' } : {})}
            optionFilterProp="children"
            filterOption={(input: string, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            style={{ width: '100%' }}
          >
            {(field.selectOptions as SelectOption[])?.map((option) => {
              const label = option.label?.toString();
              return (
                <Select.Option
                  key={option.value as any}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {t(label || '')}
                </Select.Option>
              );
            })}
          </Select>
        );
      case 'select_groups':
        return (
          <Select
            id={field.name}
            placeholder={field.placeholder}
            allowClear={!field.disableSelectClear}
            {...(field.multiSelect ? { mode: 'multiple' } : {})}
            options={field.selectOptions as SelectOptionGroup[]}
            optionFilterProp="children"
            filterOption={(input: string, option: any) => {
              const isGroup = Array.isArray(option.options);
              if (isGroup) {
                return false;
              }
              return option.label.toLowerCase().includes(input.toLowerCase());
            }}
            style={{ width: '100%' }}
          />
        );
      case 'tree_select':
        return (
          <TreeSelect
            showSearch
            style={{ width: '100%' }}
            //dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            placeholder={field.placeholder}
            allowClear={!field.disableSelectClear}
            treeDefaultExpandAll
            treeData={(field.selectOptions as SelectOption[]).map(
              (option) =>
                ({
                  title: option.label,
                  key: option.value as any,
                  children: option.children,
                } as DataNode)
            )}
          />
        );
      case 'checkbox':
        return (
          <Checkbox
            id={field.name}
            checked={field.value}
            onChange={(e) =>
              field.onChange ? field.onChange(e.target.checked) : null
            }
          >
            {field.checkboxText}
          </Checkbox>
        );
      case 'radiogroup':
        return (
          <Radio.Group>
            {(field.selectOptions as SelectOption[])?.map((option) => {
              return (
                <Radio key={option.value as any} value={option.value}>
                  {option.label}
                </Radio>
              );
            })}
          </Radio.Group>
        );
      case 'texteditor':
        return (
          <EditorComponent
            {...(field.additionalProps ? field.additionalProps : {})}
          />
        );
      case 'upload':
        return (
          <Upload.Dragger name="files" action="/upload.do">
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              ##Click or drag file to this area to upload##
            </p>
            <p className="ant-upload-hint">
              ##Support for a single or bulk upload.##
            </p>
          </Upload.Dragger>
        );
      case 'image':
        return (
          <Typography.Text type="secondary" italic>
            {t('image_upload_in_table_hint')}
          </Typography.Text>
        );
      case 'colorpicker':
        return <ColorPicker />;
    }
  };

  const fields = () => {
    const returnValue: any[] = [];
    const userIsAdmin = user?.role.some((r) => AuthRoles.bbadmin.includes(r));

    props.fields.forEach((field: FormField) => {
      const hasLabel = field.label.length && !field.type.includes('heading');
      const labelWidth = hasLabel ? 6 : 0;
      const fieldWidth = hasLabel ? 18 : 24;

      if (field.hidden && (!userIsAdmin || !field.subscription)) {
        return;
      }

      returnValue.push(
        <Form.Item
          style={{ marginBottom: 12 }}
          key={field.name}
          name={field.name}
          label={field.label}
          extra={field.extraLabel}
          hasFeedback
          rules={field.rules}
          {...(field.type === 'checkbox' ? { valuePropName: 'checked' } : {})}
          labelCol={{
            xs: { span: 24 },
            md: { span: labelWidth },
          }}
          wrapperCol={{
            xs: { span: field?.colWidth?.xs ?? 24 },
            sm: { span: field?.colWidth?.sm ?? fieldWidth },
            md: { span: field?.colWidth?.md ?? fieldWidth },
            lg: { span: field?.colWidth?.lg ?? fieldWidth },
            xl: { span: field?.colWidth?.xl ?? fieldWidth },
            xxl: { span: field?.colWidth?.xxl ?? fieldWidth },
          }}
        >
          {inputField(field)}
        </Form.Item>
      );
    });

    return returnValue;
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      onFinish={(values) => props.onSubmit(values, form.resetFields)}
      initialValues={initialValues()}
      id={props.formId}
      labelWrap
    >
      {fields()}
      {!props.formId && (
        <Form.Item wrapperCol={{ span: 12, offset: props.buttonsOffset ?? 0 }}>
          {props.backButton && (
            <Button
              type="default"
              onClick={() => {
                window.history.back();
              }}
              style={{ marginRight: 8 }}
            >
              {t('back')}
            </Button>
          )}
          <Button type="primary" htmlType="submit" loading={props.loading}>
            {props.submitButtonText ? t(props.submitButtonText) : t('save')}
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}

export default CustomForm;
